/**
 * Application Configuration — bootstrapped from the continuum server.
 *
 * The app is told ONE thing at deploy time via public/config.js
 * (window.APP_CONFIG): the continuum/hermes server URL. At startup
 * loadClientConfig() fetches the rest (environment label + name mappings) from
 * that server's /api/service_mappings, so there is no hardcoded per-environment
 * table here anymore.
 *
 * config.js may also carry a few optional overrides:
 *   - contentType: 'json' | 'protobuf'   (wire format; default 'json')
 *   - environment: string                (rare: override the server's label)
 *   - namedMailboxes: { logical: addr }  (hard override; static wins, see NameMappingService)
 *   - router.basename, app.showDemoPages (pure client deploy knobs)
 *   - qubes                               (DEPRECATED + OPTIONAL; only if this deployment still has it)
 *
 * IMPORTANT: `config` is populated asynchronously by loadClientConfig() before
 * the app renders. Any module that reads `config.*` at import time must be loaded
 * AFTER loadClientConfig() resolves (see src/index.tsx, which awaits it then
 * dynamically imports the rest of the app).
 */

// ============================================================================
// Runtime override shape (public/config.js)
// ============================================================================

export interface ClientOverrides {
  /** The continuum/hermes server URL this app bootstraps from. */
  continuumUrl?: string;
  /** Rarely set: force the environment label instead of the server's. */
  environment?: string;
  contentType?: 'json' | 'protobuf';
  namedMailboxes?: Record<string, string>;
  qubes?: { publicUrl: string; apiUrl: string };
  router?: { basename?: string };
  app?: { version?: string; showDemoPages?: boolean };
}

declare global {
  interface Window {
    APP_CONFIG?: ClientOverrides;
  }
}

// ============================================================================
// Resolved config shape
// ============================================================================

export interface AppConfig {
  environment: string;
  hermes: {
    url: string;
    contentType: 'json' | 'protobuf';
    namedMailboxes?: Readonly<Record<string, string>>;
  };
  /** Deprecated + optional. Undefined when this deployment has no qubes. */
  qubes?: {
    publicUrl: string;
    apiUrl: string;
  };
  router: {
    basename: string;
  };
  app: {
    version: string;
    showDemoPages: boolean;
  };
}

// ============================================================================
// Exported config singleton (populated by loadClientConfig)
// ============================================================================

// The fallback continuum URL when config.js omits continuumUrl. This is the API
// host. It must NEVER be the frontend's own origin: fetching /api/* from the SPA
// gets the SPA's catch-all index.html back with a 200, so a missing config presents
// as `resp.json()` choking on `<!DOCTYPE html>` instead of as a missing config.
// See loadClientConfig.
const DEFAULT_CONTINUUM_URL = 'https://continuum-api.accur8.net';

/**
 * The active configuration. Populated by loadClientConfig() before render.
 * Reading fields before loadClientConfig() resolves yields the placeholders
 * below (hermes.url === '' is the "not loaded yet" tell).
 */
export const config: AppConfig = {
  environment: 'unknown',
  hermes: { url: '', contentType: 'json' },
  router: { basename: '' },
  app: { version: 'dev', showDemoPages: false },
};

let loaded = false;

/**
 * Re-fetch config.js, bypassing the HTTP cache, and evaluate it.
 *
 * This exists to RESCUE A POISONED BROWSER. Caddy used to serve config.js as
 * `max-age=31536000, immutable` (it matched the `*.js` bundle rule), so a browser
 * that cached a bad copy will not revalidate it — not on reload, not for a YEAR.
 * The server-side header fix protects new visitors; it reaches nobody already
 * holding the poisoned entry. `cache: 'no-store'` does: it goes to the network
 * regardless of what is cached, so the next load self-heals.
 *
 * Deliberately NOT a build-stamped `?v=` on the script tag: config.js is meant to
 * be replaced at deploy time WITHOUT a rebuild, so a build-time buster would go
 * stale in exactly the case the file exists for.
 *
 * Returns the recovered overrides, or null if it could not.
 */
async function refetchConfigJs(): Promise<ClientOverrides | null> {
  try {
    const base = import.meta.env?.BASE_URL ?? '/';
    const resp = await fetch(`${base}config.js`, { cache: 'no-store' });
    if (!resp.ok) return null;
    const src = await resp.text();
    // config.js is a plain assignment to window.APP_CONFIG. Evaluate it in the
    // global scope, exactly as the <script> tag would have.
    new Function(src)();
    const recovered = (typeof window !== 'undefined' && window.APP_CONFIG) || null;
    if (recovered) {
      console.warn(
        '[AppConfig] window.APP_CONFIG was missing — recovered config.js with a ' +
          'cache-bypassing fetch. This browser was holding a stale immutable copy.',
      );
    }
    return recovered;
  } catch (e) {
    console.error('[AppConfig] could not re-fetch config.js:', e);
    return null;
  }
}

/**
 * Loads the configuration: reads config.js for the continuum URL + overrides,
 * fetches the environment label from the server, and populates `config`.
 * Idempotent. Never throws on a server fetch failure — it degrades to whatever
 * config.js provided (so a missing/unreachable server doesn't hard-block boot).
 */
export async function loadClientConfig(): Promise<void> {
  if (loaded) return;

  // window.APP_CONFIG UNSET is the poisoned tell: config.js did not run at all.
  // (An empty-ish APP_CONFIG is NOT the tell — nefario legitimately ships a
  // config.js with no continuumUrl, and must not re-fetch on every load.)
  let raw: ClientOverrides | undefined =
    typeof window !== 'undefined' ? window.APP_CONFIG : undefined;

  if (!raw) {
    raw = (await refetchConfigJs()) ?? undefined;
  }
  raw = raw ?? {};

  // The default is the API host — NEVER the frontend's own host.
  //
  // It used to be 'https://continuum.accur8.net', i.e. the SPA itself. That is the
  // amplifier that turned "config is missing" into an inscrutable failure: the app
  // fetched /api/service_mappings from ITSELF, the SPA catch-all answered with
  // index.html and a 200, and resp.json() choked on `<!DOCTYPE html>`. Login died on a
  // SyntaxError that pointed at nothing.
  //
  // A default is still needed: nefario ships a config.js that deliberately omits
  // continuumUrl (it is a different app — qubes/hermes — and never calls the continuum
  // API). Throwing here would break it. So default to somewhere that is at
  // least a real API: a wrong-but-honest 404/CORS error beats parsing your own HTML.
  const continuumUrl = raw.continuumUrl ?? DEFAULT_CONTINUUM_URL;

  let environment = raw.environment ?? 'unknown';
  try {
    const resp = await fetch(`${continuumUrl}/api/service_mappings`);
    if (resp.ok) {
      const data = await resp.json();
      if (!raw.environment && typeof data.environment === 'string' && data.environment) {
        environment = data.environment;
      }
    } else {
      console.warn(`[AppConfig] ${continuumUrl}/api/service_mappings returned ${resp.status}`);
    }
  } catch (e) {
    console.warn(`[AppConfig] failed to fetch service_mappings from ${continuumUrl}:`, e);
  }

  const built: AppConfig = {
    environment,
    hermes: {
      url: continuumUrl,
      contentType: raw.contentType ?? 'json',
      namedMailboxes: raw.namedMailboxes,
    },
    qubes: raw.qubes,
    router: { basename: raw.router?.basename ?? '' },
    app: {
      version: raw.app?.version ?? 'dev',
      showDemoPages: raw.app?.showDemoPages ?? false,
    },
  };

  Object.assign(config, built);
  loaded = true;

  console.log(
    `[AppConfig] continuumUrl=${continuumUrl} environment=${environment} ` +
      `qubes=${config.qubes ? 'on' : 'off'}`,
  );
}

/** True when this deployment has a (deprecated) qubes backend configured. */
export function isQubesConfigured(): boolean {
  return !!config.qubes?.publicUrl;
}

export default config;
